/**
 * DeleteUserModal – Confirmation modal for deleting a user.
 * Shows user info, role badge, optional warnings (tasks, records, team), and action buttons.
 * Single responsibility: present and confirm delete; parent handles API (Dependency Inversion).
 */

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { X, AlertTriangle } from 'lucide-react';
import {
  getRoleDisplayName,
  LEGACY_ROLE_DISPLAY,
  SCOPE_BADGE_CLASSES,
} from '../../constants/types/user.js';

const WARNING_ITEMS = [
  {
    key: 'assignedTasks',
    getText: () => (
      <>
        Assigned tasks will become{' '}
        <span className="font-['Roboto:SemiBold',sans-serif] font-semibold">
          unassigned
        </span>
      </>
    ),
  },
  {
    key: 'cleaningRecords',
    getText: () => (
      <>
        Historical cleaning records will be{' '}
        <span className="font-['Roboto:SemiBold',sans-serif] font-semibold">
          retained
        </span>{' '}
        but marked as deleted user
      </>
    ),
  },
  {
    key: 'teamManagement',
    getText: () => (
      <>
        Team management assignments will be{' '}
        <span className="font-['Roboto:SemiBold',sans-serif] font-semibold">
          removed
        </span>
      </>
    ),
  },
];

function getBadgeClass(user) {
  if (user?.accessScope === 'primary-organization' || user?.role === 'primary_admin')
    return SCOPE_BADGE_CLASSES['primary-organization'];
  if (user?.accessScope === 'organization' || user?.role === 'organization_admin')
    return SCOPE_BADGE_CLASSES.organization;
  if (user?.accessScope === 'multi-facility') return SCOPE_BADGE_CLASSES['multi-facility'];
  if (user?.accessScope === 'facility') return SCOPE_BADGE_CLASSES.facility;
  if (user?.role === 'admin') return SCOPE_BADGE_CLASSES.admin;
  if (user?.role === 'manager') return SCOPE_BADGE_CLASSES.manager;
  return SCOPE_BADGE_CLASSES.default;
}

function getRoleLabel(user) {
  if (user?.accessScope) return getRoleDisplayName(user.accessScope);
  return LEGACY_ROLE_DISPLAY[user?.role] ?? user?.role ?? '';
}

function getUserDisplayName(user) {
  return (
    user?.fullName?.trim() ||
    `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ||
    'User'
  );
}

export const DeleteUserModal = React.memo(function DeleteUserModal({
  isOpen,
  onClose,
  onConfirm,
  user,
  users: usersProp,
  hasAssignedTasks = false,
  hasCleaningRecords = false,
  hasTeamManagement = false,
}) {
  const handleConfirm = useCallback(() => {
    onConfirm();
    onClose();
  }, [onConfirm, onClose]);

  const isBulk = Array.isArray(usersProp) && usersProp.length > 0;
  const bulkCount = isBulk ? usersProp.length : 0;
  const displayUser = isBulk ? null : user;

  const hasAnyData = !isBulk && (hasAssignedTasks || hasCleaningRecords || hasTeamManagement);
  const badgeClass = useMemo(
    () => (displayUser ? getBadgeClass(displayUser) : ''),
    [displayUser],
  );
  const roleLabel = useMemo(
    () => (displayUser ? getRoleLabel(displayUser) : ''),
    [displayUser],
  );
  const displayName = useMemo(
    () => (displayUser ? getUserDisplayName(displayUser) : ''),
    [displayUser],
  );

  const isPrimaryAdmin =
    displayUser &&
    (displayUser.accessScope === 'primary-organization' ||
      displayUser.role === 'primary_admin' ||
      displayUser.isPrimaryAdmin);
  const isOtherAdmin =
    displayUser?.role === 'admin' &&
    displayUser?.accessScope !== 'primary-organization' &&
    displayUser?.role !== 'primary_admin';

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-[rgba(184,182,182,0.5)] z-50"
        onClick={onClose}
        role="presentation"
        aria-hidden
      />
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-[10px] px-8 py-8 w-[90vw] max-w-[540px] z-[60] shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-user-title"
      >
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" aria-hidden />
              </div>
              <h2
                id="delete-user-title"
                className="font-['Roboto:Bold',sans-serif] font-bold text-[24px] text-black"
              >
                {isBulk ? 'Delete Users' : 'Delete User'}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-[#F2F4F8] hover:bg-gray-300 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-[#1C1B1F]" />
            </button>
          </div>

          {isBulk ? (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[16px] text-black">
                {bulkCount} user{bulkCount !== 1 ? 's' : ''} selected
              </p>
              <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-600 mt-1">
                The selected users will be permanently removed.
              </p>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[16px] text-black">
                {displayName}
              </p>
              <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-600 mt-1">
                {user?.email}
              </p>
              {(user?.accessScope || user?.role) && (
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className={`inline-block px-2.5 py-1 rounded-full text-xs font-['Roboto:Medium',sans-serif] font-medium ${badgeClass}`}
                  >
                    {roleLabel}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="space-y-3">
            <p className="font-['Roboto:Medium',sans-serif] text-[15px] text-gray-800 leading-relaxed">
              {isBulk
                ? `Are you sure you want to delete ${bulkCount} user${bulkCount !== 1 ? 's' : ''}?`
                : 'Are you sure you want to delete this user?'}
            </p>

            {!isBulk && hasAnyData && (
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] text-amber-900 mb-3">
                  The following will also be affected:
                </p>
                <div className="space-y-2 ml-2">
                  {hasAssignedTasks && (
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-600" />
                      <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-amber-800">
                        {WARNING_ITEMS[0].getText()}
                      </p>
                    </div>
                  )}
                  {hasCleaningRecords && (
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-600" />
                      <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-amber-800">
                        {WARNING_ITEMS[1].getText()}
                      </p>
                    </div>
                  )}
                  {hasTeamManagement && (
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-600" />
                      <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-amber-800">
                        {WARNING_ITEMS[2].getText()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!isBulk && isPrimaryAdmin && (
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] text-red-900 mb-1 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden />
                  Primary Organization Admin Warning
                </p>
                <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-red-800">
                  This is the Primary Organization Admin. This user cannot be deleted.
                </p>
              </div>
            )}

            {!isBulk && isOtherAdmin && (
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] text-amber-900 mb-1 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden />
                  Admin User Warning
                </p>
                <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-amber-800">
                  This is an admin user with full system access. Make sure another admin exists
                  before deleting.
                </p>
              </div>
            )}

            <p className="font-['Roboto:Medium',sans-serif] text-[14px] text-gray-600 leading-relaxed flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden />
              This action cannot be undone.
            </p>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="border-2 border-gray-300 bg-white text-gray-700 rounded-lg px-6 py-2.5 font-['Roboto:SemiBold',sans-serif] font-semibold text-[15px] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="bg-red-600 text-white rounded-lg px-6 py-2.5 font-['Roboto:SemiBold',sans-serif] font-semibold text-[15px] hover:bg-red-700 transition-colors"
            >
              {isBulk ? 'Delete Users' : 'Delete User'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
});

DeleteUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  user: PropTypes.shape({
    fullName: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    accessScope: PropTypes.string,
    role: PropTypes.string,
    isPrimaryAdmin: PropTypes.bool,
  }),
  /** When set, modal shows bulk delete UI (multiple users). Can be array of user objects or empty array; length is used for count. */
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      fullName: PropTypes.string,
      email: PropTypes.string,
    }),
  ),
  hasAssignedTasks: PropTypes.bool,
  hasCleaningRecords: PropTypes.bool,
  hasTeamManagement: PropTypes.bool,
};
